import { FormattedMessage } from '@liveauctioneers/hammer-ui-core/intl';
import { BodySecondary, LabelStrong } from '@liveauctioneers/hammer-ui-core/text';
import { ReactNode } from 'react';

import { longFormatDate } from '@/utils/dates';

import {
    StyledBlue,
    StyledColumnMessage,
    StyledGreen,
    StyledGreenFull,
    StyledGrey,
    StyledGreyDark,
    StyledRed,
} from './styles';

export type MessageProps = {
    bidderId: number;
    created: number;
    hideMessage: boolean;
    limit: number;
    messageId: string;
    myBid: boolean;
    nonLaBidder: boolean;
    text: ReactNode;
    values: {
        amount: ReactNode;
        converted: ReactNode;
        limit: ReactNode;
        lotNumber: string;
        paddleNumber: string;
        serverCreatedTime?: string;
    };
};

const acceptedMessage = ({ created, messageId, values }: MessageProps) => (
    <div
        className="text-left"
        key={created}
    >
        <StyledGrey color="secondary">
            <FormattedMessage
                id={messageId}
                values={values}
            />
        </StyledGrey>
    </div>
);

const endedMessage = ({ created, text }: MessageProps) => (
    <div
        className="text-left"
        key={created}
    >
        <StyledBlue>{text}</StyledBlue>
    </div>
);

const askChangedMessage = ({ created, values }: MessageProps) => (
    <div
        className="text-left"
        key={created}
    >
        <StyledBlue>
            <FormattedMessage
                id="bidding_console.console.messages.ask_price_changed_to"
                values={values}
            />
        </StyledBlue>
    </div>
);

const missiveMessage = ({ created, text }) => {
    let missiveText: ReactNode = text;

    if (text === 'Fair Warning... This lot is about to close') {
        missiveText = <FormattedMessage id="bidding_console.console.missives.fair_warning_lot_about_to_close" />;
    } else if (text === 'GOING ONCE...GOING TWICE...') {
        missiveText = <FormattedMessage id="bidding_console.console.missives.going_once_going_twice" />;
    } else if (text === 'THIRD AND FINAL WARNING...') {
        missiveText = <FormattedMessage id="bidding_console.console.missives.third_and_final_warning" />;
    }

    return (
        <div
            className="text-left"
            key={created}
        >
            <StyledRed color="critical">{missiveText}</StyledRed>
        </div>
    );
};

const reserveMessage = ({ created, messageId, values }: MessageProps) => (
    <div
        className="text-left"
        key={created}
    >
        <StyledBlue>
            <FormattedMessage
                id={messageId}
                values={values}
            />
        </StyledBlue>
    </div>
);

const myBidMessage = ({ created, messageId, values }: MessageProps) => (
    <div
        className="text-right"
        key={created}
    >
        <StyledGreen color="secondary">
            <FormattedMessage
                id={messageId}
                values={values}
            />
        </StyledGreen>
    </div>
);

const outBidMessage = ({ created, messageId, values }: MessageProps) => (
    <div
        className="text-left"
        key={created}
    >
        <StyledGreyDark>
            <FormattedMessage
                id={messageId}
                values={values}
            />
        </StyledGreyDark>
    </div>
);

const absenteeOutBidMessage = ({ created, messageId, values }: MessageProps) => (
    <div
        className="text-left"
        key={created}
    >
        <StyledGreyDark>
            <FormattedMessage
                id={messageId}
                values={values}
            />
        </StyledGreyDark>
    </div>
);

const openedMessage = ({ created, values }: MessageProps) => (
    <div
        className="text-left"
        key={created}
    >
        <StyledBlue>
            <FormattedMessage
                id="bidding_console.console.messages.lot_opened_asking"
                values={values}
            />
        </StyledBlue>
    </div>
);

const passedMessage = ({ created, values }: MessageProps) => (
    <div
        className="text-left"
        key={created}
    >
        <StyledBlue>
            <FormattedMessage
                id="bidding_console.console.messages.lot_number_passed"
                values={values}
            />
        </StyledBlue>
    </div>
);

const reopenedMessage = ({ created, values }: MessageProps) => (
    <div
        className="text-left"
        key={created}
    >
        <StyledBlue>
            <FormattedMessage
                id="bidding_console.console.messages.lot_number_reopened"
                values={values}
            />
        </StyledBlue>
    </div>
);

const retractedMessage = ({ created, myBid, values }: MessageProps) => (
    <div
        className="text-left"
        key={created}
    >
        <StyledBlue>
            <FormattedMessage
                id={
                    myBid
                        ? 'bidding_console.console.messages.my_bid_retracted'
                        : 'bidding_console.console.messages.last_bid_retracted'
                }
                values={values}
            />
        </StyledBlue>
    </div>
);

const approvedMessage = ({ created, limit, values }: MessageProps) => (
    <div
        className="text-left"
        key={created}
    >
        <StyledGreenFull>
            <LabelStrong>
                {limit ? (
                    <FormattedMessage
                        id="bidding_console.console.messages.limited_approval"
                        values={values}
                    />
                ) : (
                    <FormattedMessage id="bidding_console.console.messages.registration_approved" />
                )}
            </LabelStrong>
        </StyledGreenFull>
    </div>
);

const skippedMessage = ({ created, values }: MessageProps) => (
    <div
        className="text-left"
        key={created}
    >
        <StyledBlue>
            <FormattedMessage
                id="bidding_console.console.messages.lot_number_skipped"
                values={values}
            />
        </StyledBlue>
    </div>
);

const soldMessage = ({ bidderId, created, myBid, values }: MessageProps) => {
    if (myBid) {
        return (
            <div
                className="text-left"
                key={created}
            >
                <StyledGreenFull>
                    <LabelStrong>
                        <FormattedMessage
                            id="bidding_console.console.messages.you_ve_won_lot"
                            values={values}
                        />
                    </LabelStrong>
                </StyledGreenFull>
            </div>
        );
    }

    return (
        <div
            className="text-left"
            key={created}
        >
            <StyledBlue>
                {bidderId === 0 ? (
                    <FormattedMessage
                        id="bidding_console.console.messages.lot_sold_competing_bidder"
                        values={values}
                    />
                ) : (
                    <FormattedMessage
                        id="bidding_console.console.messages.lot_sold_liveauctioneers_bidder"
                        values={values}
                    />
                )}
            </StyledBlue>
        </div>
    );
};

const unsoldMessage = ({ created, values }: MessageProps) => (
    <div
        className="text-left"
        key={created}
    >
        <StyledBlue>
            <FormattedMessage
                id="bidding_console.console.messages.lot_number_unsold"
                values={values}
            />
        </StyledBlue>
    </div>
);
const exceededMessage = ({ created }: MessageProps) => (
    <div
        className="text-left"
        key={created}
    >
        <StyledBlue>
            <FormattedMessage id="bidding_console.console.messages.bid_limit_exceeded" />
        </StyledBlue>
    </div>
);
const pausedMessage = ({ created, values: { serverCreatedTime } }: MessageProps) => (
    <div
        className="text-left"
        key={created}
    >
        <StyledColumnMessage>
            <LabelStrong className="mb-xs inline-block">
                <FormattedMessage id="bidding_console.console.messages.paused" />
            </LabelStrong>
            {Boolean(serverCreatedTime) && (
                <BodySecondary color="secondary">{longFormatDate(new Date(serverCreatedTime))}</BodySecondary>
            )}
        </StyledColumnMessage>
    </div>
);

const resumedMessage = ({ created, values: { serverCreatedTime } }: MessageProps) => (
    <div
        className="text-left"
        key={created}
    >
        <StyledColumnMessage>
            <LabelStrong className="mb-xs inline-block">
                <FormattedMessage id="bidding_console.console.messages.resumed" />
            </LabelStrong>
            {Boolean(serverCreatedTime) && (
                <BodySecondary color="secondary">{longFormatDate(new Date(serverCreatedTime))}</BodySecondary>
            )}
        </StyledColumnMessage>
    </div>
);

export default {
    'absentee-outbid': absenteeOutBidMessage,
    accepted: acceptedMessage,
    accepted2: acceptedMessage,
    approved: approvedMessage,
    askChanged: askChangedMessage,
    ended: endedMessage,
    exceeded: exceededMessage,
    missive: missiveMessage,
    mybid: myBidMessage,
    opened: openedMessage,
    outbid: outBidMessage,
    'outbid-absentee': outBidMessage,
    passed: passedMessage,
    paused: pausedMessage,
    reopened: reopenedMessage,
    reserve: reserveMessage,
    resumed: resumedMessage,
    retracted: retractedMessage,
    skipped: skippedMessage,
    sold: soldMessage,
    unsold: unsoldMessage,
};
